/**
 * Unified Skeleton Atom
 * @layer atom
 * @package @plerity/ui-core
 */

import { cn } from "../utils/cn";

export interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  rounded?: "sm" | "md" | "lg" | "full";
  /**
   * When `true` (default) the skeleton is its own live region: an `<output>`
   * (implicit `role="status"`) labelled "Loading". Pass `announce={false}` when
   * the skeleton sits inside a container that already announces loading (a
   * `role="status"` / `aria-busy` wrapper, `DashboardSection`, `LoadingState`,
   * or a grid of many skeletons) — it then renders as a decorative
   * `aria-hidden` block, so screen readers don't hear nested or repeated
   * "Loading" status regions.
   */
  announce?: boolean;
}

const roundedClasses: Record<string, string> = {
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full",
};

export function Skeleton({ className, width, height, rounded = "md", announce = true }: SkeletonProps) {
  // Literal gray ramp — do NOT use `bg-surface-subtle` here. It emits
  // `background-color: var(--color-bg-surface-subtle)` with NO fallback, and that
  // var is stripped from the React SPA apps' Tailwind v4 build. The literal ramp
  // keeps fallbacks (`var(--color-gray-700,#374151)`) so dark mode renders true-dark.
  const classes = cn("animate-pulse bg-gray-100 block dark:bg-gray-700", roundedClasses[rounded], className);
  if (!announce) {
    return <span className={classes} style={{ width, height }} aria-hidden="true" data-skeleton="" />;
  }
  return <output className={classes} style={{ width, height }} aria-label="Loading" data-skeleton="" />;
}

Skeleton.displayName = "Skeleton";
